import React, { useState } from 'react';
import { Box, Collapse, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { setDragData, getDragData, hasDragData } from 'Shared/flows/folders/folderUtils';
import FoundCount from 'Shared/flows/folders/FoundCount';

const FolderTreeItem = ({ folder, childrenByParent, expandedIds, level, selectedFolder, foundCounts, onSelect, onCreateChild, onRename, onDelete, onMoveFolder, onAssignFlow }) => {
    const [open, setOpen] = useState(() => expandedIds.includes(folder.id));
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const children = childrenByParent[folder.id] || [];
    const hasChildren = children.length > 0;
    const isSelected = String(selectedFolder) === String(folder.id);

    const openMenu = (e) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
    };
    const closeMenu = () => setMenuAnchor(null);

    const handleDragStart = (e) => {
        e.stopPropagation();
        setDragData(e, { kind: 'folder', id: folder.id });
    };

    const handleDragOver = (e) => {
        if (!hasDragData(e)) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const data = getDragData(e);
        if (!data) {
            return;
        }
        if (data.kind === 'folder' && String(data.id) !== String(folder.id)) {
            onMoveFolder(data.id, folder.id);
        } else if (data.kind === 'flow') {
            onAssignFlow(data.id, folder.id);
        }
    };

    return (
        <>
            <Box
                draggable
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
                onClick={() => onSelect(folder.id)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    paddingY: '6px',
                    paddingRight: '6px',
                    paddingLeft: `${8 + level * 16}px`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: isSelected ? '#289BEF' : '#3B465C',
                    backgroundColor: isDragOver ? '#d3ebfb' : (isSelected ? '#e8f4fd' : 'transparent'),
                    outline: isDragOver ? '2px dashed #289BEF' : 'none',
                    '&:hover': { backgroundColor: isDragOver ? '#d3ebfb' : (isSelected ? '#e8f4fd' : '#eef1f4') },
                    '&:hover .folder-menu-btn': { opacity: 1 },
                }}
            >
                <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                    sx={{ visibility: hasChildren ? 'visible' : 'hidden', padding: '2px', color: 'inherit' }}
                >
                    {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
                <FolderOutlinedIcon fontSize="small" sx={{ color: 'inherit' }} />
                <Typography
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: '14px',
                        fontWeight: isSelected ? 600 : 400,
                        color: 'inherit',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {folder.name}
                </Typography>
                <FoundCount count={foundCounts?.[folder.id]} />
                <IconButton
                    className="folder-menu-btn"
                    size="small"
                    onClick={openMenu}
                    sx={{ padding: '2px', opacity: 0, color: 'inherit' }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Box>

            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
                <MenuItem onClick={() => { closeMenu(); onCreateChild(folder); }}>New Subfolder</MenuItem>
                <MenuItem onClick={() => { closeMenu(); onRename(folder); }}>Rename</MenuItem>
                <MenuItem onClick={() => { closeMenu(); onDelete(folder); }} sx={{ color: '#D76A6A' }}>Delete</MenuItem>
            </Menu>

            {hasChildren && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    {children.map((child) => (
                        <FolderTreeItem
                            key={child.id}
                            folder={child}
                            childrenByParent={childrenByParent}
                            expandedIds={expandedIds}
                            level={level + 1}
                            selectedFolder={selectedFolder}
                            foundCounts={foundCounts}
                            onSelect={onSelect}
                            onCreateChild={onCreateChild}
                            onRename={onRename}
                            onDelete={onDelete}
                            onMoveFolder={onMoveFolder}
                            onAssignFlow={onAssignFlow}
                        />
                    ))}
                </Collapse>
            )}
        </>
    );
};

export default FolderTreeItem;
